import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Loader2, Star, DollarSign, Clock } from "lucide-react";
import { API } from "./utils";

function KpiCard({ label, value, accent }) {
  const tones = {
    brand: "bg-brand-surfaceAlt text-brand-deep",
    rose: "bg-rose-50 text-rose-800 border border-rose-100",
    amber: "bg-amber-50 text-amber-800 border border-amber-100",
    emerald: "bg-emerald-50 text-emerald-800 border border-emerald-100",
  };
  return (
    <div className={`rounded-2xl p-4 ${tones[accent] || tones.brand}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-70">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
    </div>
  );
}

export function InsightsPanel({ auth }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waitData, setWaitData] = useState(null);
  const [waitWindow, setWaitWindow] = useState("this_week");

  const [payrollPreset, setPayrollPreset] = useState("this_week");
  const [payroll, setPayroll] = useState(null);
  const [payrollLoading, setPayrollLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [c, r] = await Promise.all([
          fetch(`${API}/nemt/admin/analytics/cancellations?month=${month}`, {
            headers: { "X-Dispatcher-Auth": auth },
          }).then((res) => res.json()),
          fetch(`${API}/nemt/admin/analytics/ratings?month=${month}`, {
            headers: { "X-Dispatcher-Auth": auth },
          }).then((res) => res.json()),
        ]);
        setData(c);
        setRatings(r);
      } catch (err) {
        toast.error(err.message || "Failed to load insights");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [auth, month]);

  useEffect(() => {
    const loadWait = async () => {
      try {
        const res = await fetch(`${API}/nemt/admin/analytics/wait-times?window=${waitWindow}`, {
          headers: { "X-Dispatcher-Auth": auth },
        });
        const d = await res.json();
        if (res.ok) setWaitData(d);
      } catch (err) { /* silent */ }
    };
    loadWait();
  }, [auth, waitWindow]);

  useEffect(() => {
    const loadPayroll = async () => {
      setPayrollLoading(true);
      try {
        const res = await fetch(`${API}/nemt/admin/analytics/payroll?preset=${payrollPreset}`, {
          headers: { "X-Dispatcher-Auth": auth },
        });
        const d = await res.json();
        if (!res.ok) throw new Error(d?.detail || "Failed to load payroll");
        setPayroll(d);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setPayrollLoading(false);
      }
    };
    loadPayroll();
  }, [auth, payrollPreset]);

  const exportPayroll = () => {
    fetch(`${API}/nemt/admin/analytics/payroll.csv?preset=${payrollPreset}`, {
      headers: { "X-Dispatcher-Auth": auth },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `payroll-${payrollPreset}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error(err.message));
  };

  const maxCount = data?.by_reason?.[0]?.count || 1;
  const maxStarCount = ratings ? Math.max(...ratings.by_star.map((s) => s.count), 1) : 1;

  return (
    <div className="space-y-8" data-testid="insights-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700 font-semibold">Insights</div>
          <h3 className="font-serif text-2xl text-brand-deep">Cancellations, ratings & payroll</h3>
        </div>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="insights-month"
          className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm"
        />
      </div>

      {loading || !data ? (
        <div className="text-center py-16 text-brand-muted text-sm"><Loader2 className="w-5 h-5 animate-spin inline text-brand-green" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Rides created" value={data.totals.rides} accent="brand" />
            <KpiCard label="Cancelled" value={data.totals.cancelled} accent="rose" />
            <KpiCard label="Cancel rate" value={`${data.totals.rate_pct}%`} accent="amber" />
            <KpiCard label="Fees waived" value={data.totals.fees_waived_count} accent="emerald" />
          </div>

          <div className="bg-white border border-brand-navy/10 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-brand-green" />
              <h4 className="font-serif text-lg text-brand-deep">Cancellations by reason</h4>
            </div>
            {data.by_reason.length === 0 ? (
              <div className="text-sm text-brand-muted text-center py-6">No cancellations this month — nice.</div>
            ) : (
              <div className="space-y-3" data-testid="insights-reasons">
                {data.by_reason.map((r) => {
                  const pct = Math.round((r.count / maxCount) * 100);
                  return (
                    <div key={r.reason} data-testid={`insight-reason-${r.reason}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-brand-deep font-medium">{r.label}</span>
                        <span className="text-brand-muted">
                          <strong className="text-brand-deep">{r.count}</strong>
                          {r.fees_cents > 0 && <span className="ml-2 text-xs">· ${(r.fees_cents / 100).toFixed(2)} in fees</span>}
                        </span>
                      </div>
                      <div className="h-2 bg-brand-surfaceAlt rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${
                          r.reason === "doctor_cancelled" ? "bg-emerald-500"
                            : r.reason === "series_cancelled" ? "bg-brand-navy"
                              : r.reason === "no_show" ? "bg-red-500" : "bg-amber-500"
                        }`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {ratings && (
            <div className="bg-white border border-brand-navy/10 rounded-2xl p-5" data-testid="insights-ratings">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-4 h-4 text-amber-500" />
                <h4 className="font-serif text-lg text-brand-deep">Ratings this month</h4>
                <span className="ml-2 text-sm text-brand-muted">
                  {ratings.totals.rated ? (
                    <>Avg <strong className="text-brand-deep">{ratings.totals.avg}</strong> · {ratings.totals.rated} rated</>
                  ) : "No ratings yet"}
                </span>
              </div>
              {ratings.totals.rated > 0 && (
                <div className="space-y-1.5 mb-4" data-testid="insights-stars">
                  {ratings.by_star.map((s) => {
                    const pct = Math.round((s.count / maxStarCount) * 100);
                    return (
                      <div key={s.stars} className="flex items-center gap-2 text-xs">
                        <span className="w-4 text-brand-deep font-mono">{s.stars}</span>
                        <Star className={`w-3 h-3 ${s.stars >= 4 ? "fill-amber-400 text-amber-500" : "fill-slate-300 text-slate-400"}`} />
                        <div className="flex-1 h-1.5 bg-brand-surfaceAlt rounded-full">
                          <div className={`h-full rounded-full ${s.stars >= 4 ? "bg-emerald-500" : s.stars === 3 ? "bg-amber-400" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-6 text-right text-brand-muted font-mono">{s.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {ratings.recent.length > 0 && (
                <div className="divide-y divide-brand-navy/5 mt-4">
                  {ratings.recent.slice(0, 8).map((r) => (
                    <div key={r.id} className="py-2.5 flex items-start justify-between gap-3 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="text-brand-deep font-medium truncate">
                          {r.member_name || "—"} <span className="text-brand-muted text-xs">→ {r.driver_name || "driver"}</span>
                        </div>
                        {r.rating_comment && <div className="text-xs text-brand-muted italic mt-0.5">&ldquo;{r.rating_comment}&rdquo;</div>}
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating_stars ? "fill-amber-400 text-amber-500" : "text-slate-300"}`} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {data.recent.length > 0 && (
            <div className="bg-white border border-brand-navy/10 rounded-2xl p-5">
              <h4 className="font-serif text-lg text-brand-deep mb-3">Recent cancellations</h4>
              <div className="divide-y divide-brand-navy/5">
                {data.recent.map((r) => (
                  <div key={r.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="text-brand-deep font-medium truncate">{r.member_name || "—"}</div>
                      <div className="text-xs text-brand-muted">{r.trip_date} · {r.trip_time}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-brand-deep">
                        {(r.cancel_reason || "unknown").replace(/_/g, " ")}
                      </div>
                      <div className={`text-xs font-mono mt-0.5 ${r.cancellation_fee_cents ? "text-red-600" : "text-emerald-700"}`}>
                        {r.cancellation_fee_cents ? `$${(r.cancellation_fee_cents / 100).toFixed(2)}` : "waived"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Longest waits — wait-time analytics */}
      <div className="bg-white border border-brand-navy/10 rounded-2xl p-5" data-testid="wait-times-panel">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-600" />
            <h4 className="font-serif text-lg text-brand-deep">Longest waits by pickup</h4>
          </div>
          <select value={waitWindow} onChange={(e) => setWaitWindow(e.target.value)}
                  data-testid="wait-window"
                  className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm">
            <option value="this_week">This week</option>
            <option value="last_week">Last week</option>
            <option value="this_month">This month</option>
            <option value="last_30_days">Last 30 days</option>
          </select>
        </div>
        {!waitData ? (
          <div className="text-center py-6 text-sm text-brand-muted"><Loader2 className="w-4 h-4 animate-spin inline text-brand-green" /></div>
        ) : waitData.top.length === 0 ? (
          <div className="text-sm text-brand-muted text-center py-6">
            No wait-time data yet in this window. Drivers need to mark <strong>Arrived</strong> and <strong>Start ride</strong> for waits to be captured.
          </div>
        ) : (
          <>
            <div className="text-xs text-brand-muted mb-3" data-testid="wait-totals">
              <strong>{waitData.totals.rides_with_wait}</strong> rides tracked · avg wait <strong>{waitData.totals.avg_wait_min} min</strong> · longest <strong>{waitData.totals.longest_min} min</strong>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-brand-muted text-xs uppercase tracking-wider">
                    <th className="py-2 pr-3">Pickup</th>
                    <th className="py-2 pr-3 w-16 text-right">Rides</th>
                    <th className="py-2 pr-3 w-24 text-right">Avg wait</th>
                    <th className="py-2 pr-3 w-24 text-right">Longest</th>
                  </tr>
                </thead>
                <tbody>
                  {waitData.top.map((row, i) => {
                    const flag = row.avg_wait_min >= 20;
                    return (
                      <tr key={row.pickup} className="border-t border-brand-navy/5" data-testid={`wait-row-${i}`}>
                        <td className="py-2.5 pr-3 text-brand-deep min-w-0">
                          <div className="truncate max-w-md" title={row.pickup}>{row.pickup}</div>
                        </td>
                        <td className="py-2.5 pr-3 text-right text-brand-muted">{row.count}</td>
                        <td className={`py-2.5 pr-3 text-right font-mono font-semibold ${flag ? "text-red-600" : "text-brand-deep"}`}>
                          {row.avg_wait_min} min
                        </td>
                        <td className="py-2.5 pr-3 text-right text-brand-muted font-mono">{row.max_wait_min} min</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-brand-muted mt-3 leading-snug">
              Pickups with avg wait ≥ 20 min are highlighted in red — consider setting a default <code>wait_minutes</code> for those regulars so drivers don&rsquo;t get auto-flagged for no-shows.
            </p>
          </>
        )}
      </div>

      <div className="bg-white border border-brand-navy/10 rounded-2xl p-5" data-testid="payroll-panel">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <h4 className="font-serif text-lg text-brand-deep">Driver payroll</h4>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={payrollPreset} onChange={(e) => setPayrollPreset(e.target.value)}
              data-testid="payroll-preset"
              className="px-3 py-2 rounded-lg border border-brand-navy/15 text-sm">
              <option value="this_week">This week</option>
              <option value="last_week">Last week</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="last_30_days">Last 30 days</option>
            </select>
            <button onClick={exportPayroll} data-testid="payroll-export"
              className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white text-sm font-medium px-4 py-2 rounded-full transition">
              Export CSV
            </button>
          </div>
        </div>

        {payrollLoading || !payroll ? (
          <div className="text-center py-8 text-sm text-brand-muted"><Loader2 className="w-4 h-4 animate-spin inline text-brand-green" /></div>
        ) : (
          <>
            <div className="text-xs text-brand-muted mb-3">
              {payroll.window.start} → {payroll.window.end} · <strong>{payroll.totals.rides}</strong> rides · <strong>${(payroll.totals.captured_cents / 100).toFixed(2)}</strong> captured
            </div>
            {payroll.drivers.length === 0 ? (
              <div className="text-sm text-brand-muted text-center py-6">No completed rides in this window.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-brand-muted text-xs uppercase tracking-wider">
                      <th className="py-2 pr-3">Driver</th>
                      <th className="py-2 pr-3">Rides</th>
                      <th className="py-2 pr-3">Insurance</th>
                      <th className="py-2 pr-3">Private</th>
                      <th className="py-2 pr-3 text-right">Captured</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payroll.drivers.map((d) => (
                      <tr key={d.driver_id} className="border-t border-brand-navy/5" data-testid={`payroll-row-${d.driver_id}`}>
                        <td className="py-2.5 pr-3 text-brand-deep font-medium">{d.driver_name}</td>
                        <td className="py-2.5 pr-3">{d.rides}</td>
                        <td className="py-2.5 pr-3">{d.insurance_rides}</td>
                        <td className="py-2.5 pr-3">{d.private_rides}</td>
                        <td className="py-2.5 pr-3 text-right font-mono font-semibold text-emerald-700">
                          ${(d.captured_cents / 100).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
